//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';
import Redux, { Action } from 'redux';

import ClientId from '@models/ClientId';
import PartialCuid from '@models/PartialCuid';
import { createHasFeatureSelector } from '@redux/selectors/createHasFeatureSelector';
import * as shareApi from '@share/api/shareApi';
import {
  SHARE_ACTIONS,
  ShareStatusFilterValues,
} from '@share/constants/shareConstants';
import {
  IBazaarvoiceDestinationClient,
  IShareMediaItem,
  IShareModerationStats,
  ISharePublisherClient,
  IShareTaggedProductStats,
} from '@share/models/shareStateModel';
import { ReduxDispatch, ReduxGetState } from 'app/curalate.store';
import { syndicationInsightsActions } from 'app/analytics-at-a-glance/syndication-insights/redux-slice/syndicationInsightsSlice';
import { GetSharedMediaRequest } from '@share/api/shareApiModel';

export interface IShareReceiveFilteredMediaItemsSucceededAction extends Action {
  type: SHARE_ACTIONS.RECEIVE_FILTERED_SHARED_MEDIA_SUCCEEDED;
  items: IShareMediaItem[];
  totalItemCount: number;
}

export interface IShareReceiveMediaAndStatsSucceededAction extends Action {
  type: SHARE_ACTIONS.RECEIVE_SHARED_MEDIA_AND_STATS_SUCCEEDED;
  items: IShareMediaItem[];
  totalItemCount: number;
}

export interface IShareReceiveMediaItemsFailedAction extends Action {
  type: SHARE_ACTIONS.RECEIVE_SHARED_MEDIA_FAILED;
}

export interface IShareRemoveSharedMediaItemsAction extends Action {
  type: SHARE_ACTIONS.REMOVE_SHARED_ITEMS;
  cuids: PartialCuid[];
}

export interface IShareReceiveNextMediaItemsSucceededAction extends Action {
  type: SHARE_ACTIONS.RECEIVE_NEXT_SHARED_MEDIA_SUCCEEDED;
  items: IShareMediaItem[];
}

export interface IShareReceiveMediaItemsByCuidSucceededAction extends Action {
  type: SHARE_ACTIONS.RECEIVE_SHARED_MEDIA_BY_CUID_SUCCEEDED;
  items: IShareMediaItem[];
}

export interface IShareRequestFilteredMediaItemsAction extends Action {
  type: SHARE_ACTIONS.REQUEST_FILTERED_SHARED_MEDIA;
}

export interface IShareRequestMediaAndStatsAction extends Action {
  type: SHARE_ACTIONS.REQUEST_SHARED_MEDIA_AND_STATS;
}

export interface IShareRequestNextMediaItemsAction extends Action {
  type: SHARE_ACTIONS.REQUEST_NEXT_SHARED_MEDIA;
}

export interface IShareRequestMediaByCuidAction extends Action {
  type: SHARE_ACTIONS.REQUEST_SHARED_MEDIA_BY_CUID;
}

export interface IShareResetMediaAction extends Action {
  type: SHARE_ACTIONS.RESET_SHARED_MEDIA;
}

export interface IShareSetMediaLastFetchAction extends Action {
  type: SHARE_ACTIONS.SET_SHARED_MEDIA_LAST_FETCH;
  isLastFetch: boolean;
}

export interface IShareGetPublishersAction extends Action {
  type: SHARE_ACTIONS.GET_PUBLISHER_CLIENTS;
  publisherClients: ISharePublisherClient[];
}

export interface IShareModerationStatsAction extends Action {
  type: SHARE_ACTIONS.GET_MODERATION_STATS;
  stats: IShareModerationStats;
}

export interface IShareTaggedProductsStatsAction extends Action {
  type: SHARE_ACTIONS.GET_TAGGED_PRODUCTS_STATS;
  taggedProductsStats: IShareTaggedProductStats[];
}

interface IShareGetBazaarvoiceSyndicationDestinations extends Action {
  type: SHARE_ACTIONS.GET_BAZAARVOICE_DESTINATIONS;
  clients: IBazaarvoiceDestinationClient[];
}

interface IShareSelectBazaarvoiceDestination extends Action {
  type: SHARE_ACTIONS.SELECT_DESTINATION_CLIENT;
  clientId: ClientId;
}

export type ShareDataActionType =
  | IShareReceiveFilteredMediaItemsSucceededAction
  | IShareReceiveMediaAndStatsSucceededAction
  | IShareReceiveNextMediaItemsSucceededAction
  | IShareReceiveMediaItemsByCuidSucceededAction
  | IShareReceiveMediaItemsFailedAction
  | IShareRemoveSharedMediaItemsAction
  | IShareRequestFilteredMediaItemsAction
  | IShareRequestMediaAndStatsAction
  | IShareRequestNextMediaItemsAction
  | IShareRequestMediaByCuidAction
  | IShareResetMediaAction
  | IShareSetMediaLastFetchAction
  | IShareGetPublishersAction
  | IShareModerationStatsAction
  | IShareTaggedProductsStatsAction
  | IShareGetBazaarvoiceSyndicationDestinations
  | IShareSelectBazaarvoiceDestination;

function findPublisherClientId(
  clientIds: ISharePublisherClient[],
  creatorClientId: ClientId,
  isBazaarvoiceSyndicationEnabled: boolean,
): number {
  if (!clientIds.length) {
    throw new Error(`No publisherId found for clientId: ${creatorClientId}`);
  }

  if (clientIds.length > 1 && !isBazaarvoiceSyndicationEnabled) {
    throw new Error(
      `More than one publisherId found for clientId: ${creatorClientId}`,
    );
  }

  return clientIds[0].id;
}

const isBazaarvoiceSyndicationEnabledSelector = createHasFeatureSelector(
  FeatureNames.BazaarvoiceNetworkSyndicationFrontend,
);

const getSharedMedia = async (
  dispatch: ReduxDispatch,
  getState: ReduxGetState,
  offset = 0,
) => {
  const REQUEST_ITEMS_LIMIT = 30;

  const { publisherClients } = getState().share.data;

  const creatorClientId = ClientId.fromString(getState().session.client.id);
  const publisherClientId = findPublisherClientId(
    publisherClients,
    creatorClientId,
    isBazaarvoiceSyndicationEnabledSelector(getState()),
  );
  const { searchFilterValue, statusFilterValue } = getState().share.actionBar;

  const req: GetSharedMediaRequest = {
    creatorClientId: creatorClientId,
    publisherClientId: publisherClientId,
    limit: REQUEST_ITEMS_LIMIT,
    moderationStatus:
      statusFilterValue !== ShareStatusFilterValues.ALL
        ? statusFilterValue
        : undefined,
    offset,
    searchText: searchFilterValue,
  };

  const { items, totalItemCount } = await shareApi.getSharedMedia(req);

  if (items.length === 0) {
    const setSharedMediaLastFetchAction: IShareSetMediaLastFetchAction = {
      type: SHARE_ACTIONS.SET_SHARED_MEDIA_LAST_FETCH,
      isLastFetch: true,
    };
    dispatch(setSharedMediaLastFetchAction);
  }

  return { items, totalItemCount };
};

const getModerationStats = async (
  dispatch: Redux.Dispatch<ShareDataActionType>,
  getState: ReduxGetState,
) => {
  const creatorClientId = ClientId.fromString(getState().session.client.id);
  const publisherClientId = findPublisherClientId(
    getState().share.data.publisherClients,
    creatorClientId,
    isBazaarvoiceSyndicationEnabledSelector(getState()),
  );

  const moderationStatsResponse = await shareApi.getModerationStats({
    publisherClientId,
    creatorClientId,
  });

  const action: IShareModerationStatsAction = {
    type: SHARE_ACTIONS.GET_MODERATION_STATS,
    stats: moderationStatsResponse,
  };
  dispatch(action);
};

export const actions = {
  shareMedia:
    (cuids: string[], forceRequestReviewNow?: boolean) =>
    async (dispatch: ReduxDispatch, getState: ReduxGetState) => {
      const isBazaarvoiceSyndicationEnabled =
        isBazaarvoiceSyndicationEnabledSelector(getState());

      if (isBazaarvoiceSyndicationEnabled) {
        await shareApi.shareMedia(cuids);
      } else {
        const creatorClientId = ClientId.fromString(
          getState().session.client.id,
        );
        const publisherClientId = findPublisherClientId(
          getState().share.data.publisherClients,
          creatorClientId,
          isBazaarvoiceSyndicationEnabled,
        );

        await shareApi.shareMedia(
          cuids,
          publisherClientId,
          forceRequestReviewNow,
        );
      }
    },

  removeSharedMedia:
    (cuids: PartialCuid[]) =>
    async (dispatch: Redux.Dispatch<ShareDataActionType>) => {
      await shareApi.removeSharedMedia(cuids);

      return dispatch<IShareRemoveSharedMediaItemsAction>({
        type: SHARE_ACTIONS.REMOVE_SHARED_ITEMS,
        cuids,
      });
    },

  getSharedMediaAndStats:
    () => async (dispatch: Redux.Dispatch<ShareDataActionType>, getState) => {
      const requestAction: IShareRequestMediaAndStatsAction = {
        type: SHARE_ACTIONS.REQUEST_SHARED_MEDIA_AND_STATS,
      };
      dispatch(requestAction);

      await getModerationStats(dispatch, getState);
      const { items, totalItemCount } = await getSharedMedia(
        dispatch,
        getState,
      );

      const receiveAction: IShareReceiveMediaAndStatsSucceededAction = {
        type: SHARE_ACTIONS.RECEIVE_SHARED_MEDIA_AND_STATS_SUCCEEDED,
        items,
        totalItemCount,
      };
      dispatch(receiveAction);
    },

  getNextSharedMedia:
    () => async (dispatch: Redux.Dispatch<ShareDataActionType>, getState) => {
      const { flags, items: previouslyFetchedItems } = getState().share.data;
      if (
        flags.isDoneFetchingMedia ||
        flags.isFetchingMediaAndStats ||
        flags.isFetchingNextMedia ||
        flags.isFetchingFilteredMedia
      ) {
        return;
      }

      const requestAction: IShareRequestNextMediaItemsAction = {
        type: SHARE_ACTIONS.REQUEST_NEXT_SHARED_MEDIA,
      };
      dispatch(requestAction);

      const offset = previouslyFetchedItems.length;
      const { items } = await getSharedMedia(dispatch, getState, offset);

      const receiveAction: IShareReceiveNextMediaItemsSucceededAction = {
        type: SHARE_ACTIONS.RECEIVE_NEXT_SHARED_MEDIA_SUCCEEDED,
        items,
      };
      dispatch(receiveAction);
    },

  getFilteredSharedMedia:
    () => async (dispatch: Redux.Dispatch<ShareDataActionType>, getState) => {
      const { flags } = getState().share.data;
      if (
        flags.isFetchingMediaAndStats ||
        flags.isFetchingNextMedia ||
        flags.isFetchingFilteredMedia
      ) {
        return;
      }

      const setSharedMediaLastFetchAction: IShareSetMediaLastFetchAction = {
        type: SHARE_ACTIONS.SET_SHARED_MEDIA_LAST_FETCH,
        isLastFetch: false,
      };
      dispatch(setSharedMediaLastFetchAction);

      const requestAction: IShareRequestFilteredMediaItemsAction = {
        type: SHARE_ACTIONS.REQUEST_FILTERED_SHARED_MEDIA,
      };
      dispatch(requestAction);

      const { items, totalItemCount } = await getSharedMedia(
        dispatch,
        getState,
      );

      const receiveAction: IShareReceiveFilteredMediaItemsSucceededAction = {
        type: SHARE_ACTIONS.RECEIVE_FILTERED_SHARED_MEDIA_SUCCEEDED,
        items,
        totalItemCount,
      };
      dispatch(receiveAction);
    },

  getSharedMediaByCuid:
    (cuid: string) =>
    async (
      dispatch: Redux.Dispatch<ShareDataActionType>,
      getState: ReduxGetState,
    ) => {
      const { publisherClients } = getState().share.data;

      const creatorClientId = ClientId.fromString(getState().session.client.id);
      const publisherClientId = findPublisherClientId(
        publisherClients,
        creatorClientId,
        isBazaarvoiceSyndicationEnabledSelector(getState()),
      );

      const req: GetSharedMediaRequest = {
        creatorClientId,
        publisherClientId,
        cuid,
      };

      const requestAction: IShareRequestMediaByCuidAction = {
        type: SHARE_ACTIONS.REQUEST_SHARED_MEDIA_BY_CUID,
      };
      dispatch(requestAction);

      const { items } = await shareApi.getSharedMedia(req);

      const receiveAction: IShareReceiveMediaItemsByCuidSucceededAction = {
        type: SHARE_ACTIONS.RECEIVE_SHARED_MEDIA_BY_CUID_SUCCEEDED,
        items,
      };
      dispatch(receiveAction);
    },

  getPublishersForClient:
    () => async (dispatch: Redux.Dispatch<ShareDataActionType>, getState) => {
      const creatorClientId = getState().session.client.id;
      const publisherClientsResponse =
        await shareApi.getPublishersForCreator(creatorClientId);

      const action: IShareGetPublishersAction = {
        type: SHARE_ACTIONS.GET_PUBLISHER_CLIENTS,
        publisherClients: publisherClientsResponse.publisherClients,
      };
      dispatch(action);
    },

  getModerationStats:
    () => (dispatch: Redux.Dispatch<ShareDataActionType>, getState) => {
      return getModerationStats(dispatch, getState);
    },

  getTaggedProductsStats:
    () =>
    async (
      dispatch: Redux.Dispatch<ShareDataActionType>,
      getState: ReduxGetState,
    ) => {
      const creatorClientId = ClientId.fromString(getState().session.client.id);
      const publisherClientId = findPublisherClientId(
        getState().share.data.publisherClients,
        creatorClientId,
        isBazaarvoiceSyndicationEnabledSelector(getState()),
      );

      const response = await shareApi.getTaggedProductsStats(
        publisherClientId,
        creatorClientId,
      );

      const action: IShareTaggedProductsStatsAction = {
        type: SHARE_ACTIONS.GET_TAGGED_PRODUCTS_STATS,
        taggedProductsStats:
          response.taggedProducts as IShareTaggedProductStats[],
      };
      dispatch(action);
    },

  resetSharedMedia: () => {
    const shareResetMediaAction: IShareResetMediaAction = {
      type: SHARE_ACTIONS.RESET_SHARED_MEDIA,
    };
    return shareResetMediaAction;
  },

  getBazaarvoiceSyndicationDestinations:
    () => async (dispatch: ReduxDispatch, getState: ReduxGetState) => {
      const sourceClientId = ClientId.fromString(getState().session.client.id);
      const { clients } =
        await shareApi.getBazaarvoiceNetworkDestinations(sourceClientId);

      const action: IShareGetBazaarvoiceSyndicationDestinations = {
        type: SHARE_ACTIONS.GET_BAZAARVOICE_DESTINATIONS,
        clients,
      };
      dispatch(action);
    },

  selectDestinationClient:
    (newDestinationClient: ClientId) => async (dispatch: ReduxDispatch) => {
      const action: IShareSelectBazaarvoiceDestination = {
        type: SHARE_ACTIONS.SELECT_DESTINATION_CLIENT,
        clientId: newDestinationClient,
      };

      dispatch(action);
    },

  loadBazaarvoiceSyndicationInsightsData:
    () => async (dispatch: ReduxDispatch, getState: ReduxGetState) => {
      await dispatch(actions.getBazaarvoiceSyndicationDestinations());

      const destinations = getState().share.data.publisherClients;
      if (destinations.length > 0) {
        await dispatch(actions.getSharedMediaAndStats());
      } else {
        dispatch(
          syndicationInsightsActions.setEmtpyStateReason(
            "You don't have any client relationships setup in Bazaarvoice yet. Please contact your account manager for assistance.",
          ),
        );
      }
    },
};
